import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Public API
export const publicApi = axios.create({
  baseURL: BACKEND_URL,
  timeout: 30000,
});

// Admin API — sends/receives cookies for session auth
export const adminApi = axios.create({
  baseURL: BACKEND_URL,
  timeout: 30000,
  withCredentials: true,
});

// If we ever get a 401 on an admin endpoint, the session is dead.
// Bounce the user to /admin/login (preserving where they were).
adminApi.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || '';
    // Don't redirect on the me/login probe (let AdminAuthContext handle initial auth state)
    if (status === 401 && !url.endsWith('/api/admin/auth/me') && !url.endsWith('/api/admin/auth/login')) {
      // Only redirect if we're inside the admin area
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin') && !window.location.pathname.startsWith('/admin/login')) {
        try { sessionStorage.setItem('admin_redirect_after_login', window.location.pathname); } catch { /* ignore */ }
        window.location.replace('/admin/login');
      }
    }
    return Promise.reject(err);
  }
);

export const GALLERY = {
  publicList: () => publicApi.get('/api/gallery/projects').then((r) => r.data),
  publicDetail: (slug) => publicApi.get(`/api/gallery/projects/${slug}`).then((r) => r.data),
};

export const ADMIN = {
  login: (email, password) => adminApi.post('/api/admin/auth/login', { email, password }).then((r) => r.data),
  logout: () => adminApi.post('/api/admin/auth/logout').then((r) => r.data),
  me: () => adminApi.get('/api/admin/auth/me').then((r) => r.data),
  overview: () => adminApi.get('/api/admin/overview').then((r) => r.data),
  leads: (params = {}) => adminApi.get('/api/admin/leads', { params }).then((r) => r.data),
  updateLeadStatus: (id, source, status) =>
    adminApi.patch(`/api/admin/leads/${id}/status`, { source, status }).then((r) => r.data),
  addLeadNote: (id, source, text) =>
    adminApi.post(`/api/admin/leads/${id}/notes`, { source, text }).then((r) => r.data),
  fullLead: (source, id) => adminApi.get(`/api/admin/leads/${source}/${id}/full`).then((r) => r.data),
  apps: (params = {}) => adminApi.get('/api/admin/applications', { params }).then((r) => r.data),
  app: (id) => adminApi.get(`/api/admin/applications/${id}`).then((r) => r.data),
  appResume: (id) => adminApi.get(`/api/admin/applications/${id}/resume`).then((r) => r.data),
  updateAppStatus: (id, status) =>
    adminApi.patch(`/api/admin/applications/${id}/status`, { status }).then((r) => r.data),
  addAppNote: (id, text) =>
    adminApi.post(`/api/admin/applications/${id}/notes`, { text }).then((r) => r.data),
  contacts: (params = {}) => adminApi.get('/api/admin/contacts', { params }).then((r) => r.data),
  contact: (id) => adminApi.get(`/api/admin/contacts/${id}`).then((r) => r.data),
  updateContactStatus: (id, status) =>
    adminApi.patch(`/api/admin/contacts/${id}/status`, { status }).then((r) => r.data),
  blog: () => adminApi.get('/api/admin/blog').then((r) => r.data),
  createBlog: (payload) => adminApi.post('/api/admin/blog', payload).then((r) => r.data),
  deleteBlog: (slug) => adminApi.delete(`/api/admin/blog/${slug}`).then((r) => r.data),
  setBlogStatus: (slug, status) =>
    adminApi.patch(`/api/admin/blog/${slug}/status`, { status }).then((r) => r.data),
  team: () => adminApi.get('/api/admin/team').then((r) => r.data),
  inviteTeam: (payload) => adminApi.post('/api/admin/team/invite', payload).then((r) => r.data),
  updateTeam: (id, payload) => adminApi.patch(`/api/admin/team/${id}`, payload).then((r) => r.data),
  deleteTeam: (id) => adminApi.delete(`/api/admin/team/${id}`).then((r) => r.data),
  changePassword: (current_password, new_password) =>
    adminApi.post('/api/admin/settings/password', { current_password, new_password }).then((r) => r.data),
  integrations: () => adminApi.get('/api/admin/settings/integrations').then((r) => r.data),
  publishLog: (params = {}) => adminApi.get('/api/admin/publish-log', { params }).then((r) => r.data),
  // Gallery
  galleryStatus: () => adminApi.get('/api/admin/gallery/status').then((r) => r.data),
  galleryOauthUrl: (redirect_uri) => adminApi.get('/api/admin/gallery/oauth/url', { params: { redirect_uri } }).then((r) => r.data),
  galleryOauthExchange: (payload) => adminApi.post('/api/admin/gallery/oauth/exchange', payload).then((r) => r.data),
  galleryDisconnect: () => adminApi.post('/api/admin/gallery/disconnect').then((r) => r.data),
  gallerySync: () => adminApi.post('/api/admin/gallery/sync').then((r) => r.data),
  galleryProjects: () => adminApi.get('/api/admin/gallery/projects').then((r) => r.data),
  galleryHide: (slug, hidden) => adminApi.patch(`/api/admin/gallery/projects/${slug}/hide`, { hidden }).then((r) => r.data),
  galleryRename: (slug, display_name) => adminApi.patch(`/api/admin/gallery/projects/${slug}/rename`, { display_name }).then((r) => r.data),
};

export const PUBLIC_FORMS = {
  careersPositions: () => publicApi.get('/api/careers/positions').then((r) => r.data),
  submitApplication: (formData) =>
    publicApi.post('/api/careers/apply', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data),
  submitContact: (payload) => publicApi.post('/api/contact/submit', payload).then((r) => r.data),
};
